/* eslint-disable no-unused-vars */
/* eslint-disable import/no-unresolved */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Stack, Button, Grid, Box, IconButton } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { Icon } from '@iconify/react';
import { ACTIONS } from '../../config/const.config';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/manage-categories.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import ConfirmPopup from '../common/confirm-popup.component';
import SnackbarInfo from '../common/snackbar-info.component';
import validationRules from '../../utils/validations.util';
import SearchableCategory from '../form-elements/searchableCategory.component';
import ROUTE_PATH from '../../config/routes.config';
import editUrl from '../../utils/url-edit.util';
import {
  assignCollectionCategories,
  deleteAssignedCategoriesById,
  viewCollectionCategoriesById,
} from '../../services/content.service';

const ManageCollectionCategories = ({ id }) => {
  const columns = LISTING_COLUMNS;
  const actions = LISTING_ACTIONS;
  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      categories: [],
    },
  });
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    channel: 0,
    filtersCounter: 0,
    collectionName: '',
  });

  const [refetchData, setRefetchData] = useState(false);
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    if (options.loading) return;
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };
  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteAssignedCategoriesById(id, doAction.data.id)
      .then(() => {
        handleActionSuccess('Assigned category deleted successfully.');
        handleRefreshData();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const onFormSubmit = (data) => {
    if (formSubmitted) return;
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    const selectedCategories = [];
    for (let i = 0; i < data.categories.length; i += 1) {
      selectedCategories.push(data.categories[i].id);
    }
    const payload = {
      categories: selectedCategories,
    };
    assignCollectionCategories(id, payload)
      .then((res) => {
        if (res.data) {
          handleRefreshData();
          handleActionSuccess(' Category assigned successfully.');
          setValue('categories', []);
          setFormSubmitted(false);
        }
      })
      .catch((e) => {
        const message =
          e.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const handleOpenPage = () => {
    const url = editUrl(
      ROUTE_PATH.CONTENT_COLLECTION_MANAGE_CATEGORY_ORDER,
      id
    );
    navigate(url);
  };

  useEffect(() => {
    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    viewCollectionCategoriesById(id, paramsQuery)
      .then((res) => {
        setOptions({
          ...options,
          loading: false,
          totalRows: res?.data?.totalRows || 0,
          rows: res?.data?.rows || [],
          collectionName: res?.data?.collectionName || '',
          channel: res?.data?.channel?.id || {},
          error: false,
        });
        setRefetchData((prev) => !prev);
        setDataLoaded(true);
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          collectionName: '',
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 1, py: 1 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 1, py: 1 }}
        >
          <Link to={ROUTE_PATH.CONTENT_COLLECTIONS}>
            <IconButton color="primary" variant="contained">
              <Icon icon="material-symbols:arrow-back-ios-new" />
            </IconButton>
          </Link>
          <Box
            sx={{ marginLeft: 0.2 }}
            fontWeight="bold"
            fontSize="20px"
          >{`Manage Categories ${
            options.collectionName ? `- ${options.collectionName}` : ''
          }`}</Box>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 1, py: 1 }}
      >
        <Box
          width="50%"
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Box width="90%">
            <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="categories"
                  control={control}
                  rules={validationRules.REQUIRED}
                  defaultValue={[]}
                  render={({ field: { onChange, value } }) => (
                    <SearchableCategory
                      id="categories"
                      name="categories"
                      label="Categories"
                      defaultValue={value}
                      multiple
                      onChange={onChange}
                      sx={{ width: '100%', my: 1 }}
                      selectedChannel={options.channel}
                      refetchData={refetchData}
                      error={errors?.categories?.message || ''}
                      from="collection"
                    />
                  )}
                />
              </Grid>
            </form>
          </Box>

          <Button
            type="submit"
            color="primary"
            form="add-edit-form"
            variant="contained"
            startIcon={<Iconify icon="ic:round-add" />}
            disabled={formSubmitted}
            sx={{ marginLeft: 2 }}
          >
            Assign
          </Button>
        </Box>

        <Box
          width="40%"
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button
            color="primary"
            variant="contained"
            onClick={() => handleOpenPage()}
            sx={{ marginRight: 2 }}
            disabled={options.loading}
          >
            Manage Category Order
          </Button>
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </Box>
      </Stack>

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
        />
      </Card>

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title="Delete Assigned Category"
          message="Do you want to delete this record? You can not undo this action!"
          noteText=""
          onCancel={handleActionCancel}
          onSuccess={handleDeleteData}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ManageCollectionCategories.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ManageCollectionCategories;
